import { useMutation } from '@sveltestack/svelte-query';
import type { UseMutationOptions } from '@sveltestack/svelte-query';
import { HTTPError } from 'ky';
import api from '../services/kyAuthenticatedInstance';
import type { IConditionModels } from '../models/IConditionModels';
import type { IProjectModel, MastPointUpdateResponse } from '../models/IProjectModel';
import type {
	IMastPointFullModel,
	IMastPointPutModel,
	IMastPointConstructionPutModel
} from '../models/IMastPointFullModel';
import type { ICompanyModel } from '../models/ICompanyModel';
import type { IGeoJsonPolygonModel } from '$lib/models/IGeoJsonPolygonModel';
import type {
	ISectionCalculationCircuit,
	ISectionCalculationPayload
} from '$lib/core/map/map-tools-store';
import type {
	IAddPartnershipModel,
	IUpdatePartnershipModel
} from '$lib/models/IPartnerCompaniesModel';
import { getPlanQueryParameter, type IPlanMetaInProject } from '$lib/core/map/map-mode-store';
import { get } from 'svelte/store';
import type { projectAndPlanStatus } from '$lib/utils/constants';
import type {
	ComponentControlStatusPayload,
	UpdatableChecklistCondition
} from '$lib/models/IChecklistModel';
import type { APIErrorModel } from '$lib/models/APIErrorModel';
import type { IDSSLinesModel } from '$lib/models/IDSSLinesModel';
import type { ISmartData } from '$lib/models/ISmartData';

export type ImageUploadDataEnrichment = {
	fileName: string;
	description: string;
	filePath: string;
	coordinates: {
		latitude: number | null;
		longitude: number | null;
		height: number | null;
	};
	capturedAt: string | null;
	direction: number | null;
	directionRef: string | null;
};

/* export const deleteDamageReportMutation = () => useMutation<AxiosResponse<any>, AxiosError, { damageId: number }>('deleteDamageReportMutation',
  ({ damageId }) =>
    axiosAuthenticatedInstance.delete(`/Damages/${damageId}`)
); */

export const deleteDamageReportMutation = () =>
	useMutation<Response, HTTPError, { damageId: number }>(
		'deleteDamageReportMutation',
		({ damageId }) => api.delete(`Damages/${damageId}`)
	);

const conditions = {
	useUpdateConditionTemplateMutation: <T>() =>
		useMutation<T, HTTPError, { template: IConditionModels.IConditionTemplate }>(
			'putConditionTemplate',
			({ template }) =>
				api.put(`ConditionReport/Templates/${template.id}`, { json: template }).json()
		),
	useAddConditionTemplateMutation: <T>() =>
		useMutation<T, HTTPError, { template: T }>(
			'postConditionTemplate',
			({ template }: { template: T }) =>
				api.post(`ConditionReport/Templates`, { json: template }).json()
		),
	useDeleteConditionTemplateMutation: <T>() =>
		useMutation<T, HTTPError, { templateId: number }>('deleteConditionTemplate', ({ templateId }) =>
			api.delete(`ConditionReport/Templates/${templateId}`).json()
		),
	useUpdateConditionMutation: <T>() =>
		useMutation<T, HTTPError, { data: UpdatableChecklistCondition }>('putCondition', ({ data }) =>
			api
				.put(`Condition`, {
					json: data
				})
				.json()
		),
	useRectifyConditionMutation: <T>() =>
		useMutation<
			T,
			HTTPError,
			{ conditionId: number; rectified: boolean; comment: string },
			unknown
		>(
			'rectifyConditionMutation',
			(data: { conditionId: number; rectified: boolean; comment: string }) =>
				api
					.put(`Condition/RectifyCondition` + get(getPlanQueryParameter), {
						json: data
					})
					.json()
		),
	useDeleteConditionReportMutation: <T>() =>
		useMutation<T, HTTPError, { reportId: number }>('deleteConditionReport', ({ reportId }) =>
			api.delete(`ConditionReport/${reportId}`).json()
		),
	useDeleteConditionMutation: <T>() =>
		useMutation<T, HTTPError, { conditionId: number }>(
			'deleteCondtion',
			({ conditionId }: { conditionId: number }) => api.delete(`Condition/${conditionId}`).json()
		),
	useAddConditionReportFileMutation: <T>(
		mutationOptions?: UseMutationOptions<
			T,
			HTTPError,
			{ formData: FormData; conditionId: number },
			unknown
		>
	) =>
		useMutation<T, HTTPError, { formData: FormData; conditionId: number }>(
			'addConditionReportFileMutation',
			({ formData, conditionId }: { formData: FormData; conditionId: number }) =>
				api
					.post(`Condition/file/${conditionId}`, {
						body: formData
					})
					.json(),
			mutationOptions
		),
	useDeleteConditionReportImageFileMutation: <T>() =>
		useMutation<T, HTTPError, { conditionReportId: number; fileToDeleteId: number }>(
			'deleteConditionReportImageFile',
			({
				conditionReportId,
				fileToDeleteId
			}: {
				conditionReportId: number;
				fileToDeleteId: number;
			}) => {
				return api
					.delete(`ConditionReport/${conditionReportId}/deleteImage`, {
						json: {
							conditionReportId,
							fileToDeleteId
						}
					})
					.json();
			}
		),
	useUpdateComponentControlStatusMutation: <T>() =>
		useMutation<T, HTTPError, ComponentControlStatusPayload>(
			'updateComponentControlStatus',
			(payload: ComponentControlStatusPayload) =>
				api
					.put('Condition/ComponentControlStatus' + get(getPlanQueryParameter), {
						json: payload
					})
					.json()
		)
};

const projects = {
	useUpdateProjectMutation: <T>() =>
		useMutation<T, HTTPError, { project: IProjectModel.NewProject }>('putProject', ({ project }) =>
			api.put(`Projects/${project.id}`, { json: project }).json()
		),
	useAddProjectMutation: <T>() =>
		useMutation<number, HTTPError, { project: IProjectModel.NewProject }>(
			'postProject',
			({ project }: { project: IProjectModel.NewProject }) =>
				api.post(`Projects`, { json: project }).json()
		),
	useAddPlanMutation: <T>() =>
		useMutation<number, HTTPError, { plan: IProjectModel.NewPlan }>(
			'postPlan',
			({ plan }: { plan: IProjectModel.NewPlan }) => api.post(`Plans`, { json: plan }).json()
		),
	useDeleteProjectMutation: <T>() =>
		useMutation<T, HTTPError, { projectId: number }>('deleteProject', ({ projectId }) =>
			api.delete(`Projects/${projectId}?forceDelete=true`).json()
		),
	useDeletePlanMutation: <T>() =>
		useMutation<T, HTTPError, { planId: number }>('deletePlan', ({ planId }) =>
			api.delete(`Plans/${planId}?forceDelete=true`).json()
		),
	useDeletePlansMutation: <T>() =>
		useMutation<T, HTTPError, { planIds: number[] }>('deletePlans', ({ planIds }) =>
			api.post(`Plans/DeletePlans`, { json: { planIds } }).json()
		),
	useFetchProjectDataMutation: <T>() =>
		useMutation<T, HTTPError, { projectId: number }>(['getProject'], ({ projectId }) =>
			api.get(`Projects/${projectId}/plans`).json()
		),
	useUpdatePlanMutation: <T>() =>
		useMutation<T, HTTPError, { plan: IPlanMetaInProject }>('putPlan', ({ plan }) =>
			api.put(`Plans/${plan.id}`, { json: plan }).json()
		),
	useUpdatePlanStatusMutation: <T>() =>
		useMutation<
			T,
			HTTPError,
			{ planId: number; planStatus: (typeof projectAndPlanStatus)[number]['value'] }
		>('useUpdatePlanStatusMutation', ({ planId, planStatus }) =>
			api.put(`Plans/status/${planId}`, { json: { status: planStatus } }).json()
		),
	useAddDrawingMutation: <T>() =>
		useMutation<
			T,
			HTTPError,
			{ planId: number; payload: { czmlDrawings: any[]; collectionName: string } }
		>(
			'useAddDrawingMutation',
			({
				planId,
				payload
			}: {
				planId: number;
				payload: { czmlDrawings: any[]; collectionName: string };
			}) => api.post(`Plans/drawings/${planId}`, { json: payload }).json()
		),
	useUpdateDrawingMutation: <T>() =>
		useMutation<
			T,
			HTTPError,
			{
				planId: number;
				payload: { czmlDrawings: any[]; collectionName: string; planDrawingId: number };
			}
		>(
			'useUpdateDrawingMutation',
			({
				planId,
				payload
			}: {
				planId: number;
				payload: { czmlDrawings: any[]; collectionName: string; planDrawingId: number };
			}) => api.put(`Plans/drawings/${planId}`, { json: payload }).json()
		),
	useDeleteDrawingMutation: <T>() =>
		useMutation<T, HTTPError, { planId: number; drawingId: number }>(
			'useDeleteDrawingMutation',
			({ planId, drawingId }: { planId: number; drawingId: number }) =>
				api.delete(`Plans/drawings/${planId}/${drawingId}`).json()
		),
	// Returns the id of the newly created plan copy.
	useCopyPlanMutation: () =>
		useMutation<number, HTTPError, { planId: number; planName: string }>(
			'copyPlan',
			({ planId, planName }: { planId: number; planName: string }) =>
				api.post(`Plans/copy/${planId}`, { json: planName }).json<number>()
		)
};

const mastPoints = {
	useUpdateMastPointMutation: <T>() =>
		useMutation<
			T,
			HTTPError,
			{ mastId: number; payload: Partial<IMastPointPutModel.MastpointPutFullModel> }
		>(
			'putMastPoint',
			({
				mastId,
				payload
			}: {
				mastId: number;
				payload: Partial<IMastPointPutModel.MastpointPutFullModel>;
			}) =>
				api
					.put(`MastPoint/${mastId}/czml` + get(getPlanQueryParameter), { json: payload, retry: 0 })
					.json()
		),
	useDeleteMastpointMutation: <T>() =>
		useMutation<T, HTTPError, { mastPointId: IMastPointFullModel.MastPoint['id'] }>(
			'deleteMastPoint',
			({ mastPointId }: { mastPointId: IMastPointFullModel.MastPoint['id'] }) =>
				api.delete(`MastPoint/${mastPointId}` + get(getPlanQueryParameter)).json()
		),
	useUpdateVirtualToPhysicalPolesForMastpointMutation: <T>() =>
		useMutation<T, HTTPError, { mastPointId: IMastPointFullModel.MastPoint['id'] }>(
			'updateVirtualToPhysicalPolesForMastpoint',
			({ mastPointId }: { mastPointId: IMastPointFullModel.MastPoint['id'] }) =>
				api
					.put(`MastPoint/${mastPointId}/VirtualPolesToNonVirtual` + get(getPlanQueryParameter))
					.json()
		),
	useUpdateMastpointRotationMutation: <T>() =>
		useMutation<T, HTTPError, { mastId: number; payload: { bearingOffset: number } }>(
			'useUpdateMastpointRotationMutation',
			({ mastId, payload }: { mastId: number; payload: { bearingOffset: number } }) =>
				api
					.put(`MastPoint/${mastId}/bearing/czml` + get(getPlanQueryParameter), { json: payload })
					.json()
		),
	useNewMastpointConstructionTemplate: <T>() =>
		useMutation<T, HTTPError, { payload: { mastId: number; name: string } }>(
			'useNewMastpointConstructionTemplate',
			({ payload }: { payload: { mastId: number; name: string } }) =>
				api.post(`MastPoint/construction`, { json: payload }).json()
		),
	useUpdateMastpointConstruction: () =>
		useMutation<
			MastPointUpdateResponse,
			HTTPError,
			{ mastId: number; payload: IMastPointConstructionPutModel.Construction }
		>(
			'useUpdateMastpointConstruction',
			({
				mastId,
				payload
			}: {
				mastId: number;
				payload: IMastPointConstructionPutModel.Construction;
			}) =>
				api
					.put(`MastPoint/${mastId}/construction/czml` + get(getPlanQueryParameter), {
						json: payload
					})
					.json()
		)
};

const poles = {
	addNewPoleMutation: <T>({
		mutationOptions
	}: {
		mutationOptions?: UseMutationOptions<
			T,
			HTTPError,
			{ mastId: number; payload: { operationalMarking: string } },
			unknown
		>;
	}) =>
		useMutation<T, HTTPError, { mastId: number; payload: { operationalMarking: string } }>(
			'addNewPoleMutation',
			({ mastId, payload }: { mastId: number; payload: { operationalMarking: string } }) =>
				api
					.post(`MastPoint/pole/czml` + get(getPlanQueryParameter) + '&mastId=' + mastId, {
						json: payload
					})
					.json(),
			mutationOptions
		),
	useUpdatePoleMutation: <T>({
		mutationOptions
	}: {
		mutationOptions?: UseMutationOptions<
			T,
			HTTPError,
			{ mastId: number; payload: IMastPointPutModel.PolePutData },
			unknown
		>;
	}) =>
		useMutation<T, HTTPError, { mastId: number; payload: IMastPointPutModel.PolePutData }>(
			'useUpdatePoleMutation',
			({ mastId, payload }: { mastId: number; payload: IMastPointPutModel.PolePutData }) =>
				api
					.put(`MastPoint/${mastId}/czml` + get(getPlanQueryParameter), {
						json: {
							pole: payload
						}
					})
					.json(),
			mutationOptions
		),
	useUpdatePoleConstructionMutation: <T>({
		mutationOptions
	}: {
		mutationOptions?: UseMutationOptions<
			T,
			HTTPError,
			{ mastId: number; payload: IMastPointPutModel.PoleConstructionPutData },
			unknown
		>;
	}) =>
		useMutation<
			T,
			HTTPError,
			{ mastId: number; payload: IMastPointPutModel.PoleConstructionPutData }
		>(
			'useUpdatePoleConstructionMutation',
			({
				mastId,
				payload
			}: {
				mastId: number;
				payload: IMastPointPutModel.PoleConstructionPutData;
			}) =>
				api
					.put(`MastPoint/${mastId}/czml` + get(getPlanQueryParameter), {
						json: {
							polesConstruction: payload
						}
					})
					.json(),
			mutationOptions
		),
	useTerrainProfileMutation: () =>
		useMutation<Blob, HTTPError, { mastpointIds: number[] }>(
			'useTerrainProfileMutation',
			({ mastpointIds }: { mastpointIds: number[] }) =>
				api
					.post(`Reports/File/TerrainProfile`, {
						json: {
							MastPointIds: mastpointIds
						}
					})
					.blob()
		),
	useUpdatePoleDiameters: <T>() =>
		useMutation<T, HTTPError, { payload: { poleId: number; diameter: number }[] }>(
			'useUpdatePoleDiameters',
			({ payload }: { payload: { poleId: number; diameter: number }[] }) =>
				api
					.put(`MastPoint/Poles/diameters`, {
						json: { poleDiameters: payload }
					})
					.json()
		)
};

type BatchSupportsPayload = {
	mastId: number;
	mastConstruction: { isAnchored: boolean };
	supportWires: IMastPointPutModel.SupportWirePayload[];
};

const supports = {
	useUpdateSupportsMutation: <T>({
		mutationOptions
	}: {
		mutationOptions?: UseMutationOptions<
			T,
			HTTPError,
			{ mastId: number; payload: IMastPointPutModel.SupportWirePayload },
			unknown
		>;
	}) =>
		useMutation<T, HTTPError, { mastId: number; payload: IMastPointPutModel.SupportWirePayload }>(
			'useUpdateSupportsMutation',
			({ mastId, payload }: { mastId: number; payload: IMastPointPutModel.SupportWirePayload }) =>
				api
					.put(`MastPoint/${mastId}/czml` + get(getPlanQueryParameter), {
						json: { supportWires: [payload] }
					})
					.json(),
			mutationOptions
		),

	/**
	 * Batch variant used by the Bardunhjelper: sends the whole proposed
	 * `supportWires` array plus the `mastConstruction.isAnchored` flag in one
	 * PUT, matching the backend's `MastpointPutFullModel` envelope.
	 */
	useBatchUpdateSupportsMutation: <T>({
		mutationOptions
	}: {
		mutationOptions?: UseMutationOptions<T, HTTPError, BatchSupportsPayload, unknown>;
	}) =>
		useMutation<T, HTTPError, BatchSupportsPayload>(
			'useBatchUpdateSupportsMutation',
			({ mastId, mastConstruction, supportWires }: BatchSupportsPayload) =>
				api
					.put(`MastPoint/${mastId}/czml` + get(getPlanQueryParameter), {
						json: { mastConstruction, supportWires: supportWires }
					})
					.json(),
			mutationOptions
		)
};

const lines = {
	useAutoOrganizeLinesPolygonMutation: <ContextualizedCzmlResponse>() =>
		useMutation<
			ContextualizedCzmlResponse,
			HTTPError,
			{ polygon: IGeoJsonPolygonModel['polygon'] }
		>('autoOrganizeLinesArea', ({ polygon }: { polygon: IGeoJsonPolygonModel['polygon'] }) =>
			api
				.post(`Lines/UpdateLineOffsetsForArea/czml` + get(getPlanQueryParameter), { json: polygon })
				.json()
		),
	useInsertLinesMutation: <T>() =>
		useMutation<T, HTTPError, { payload: IDSSLinesModel.Root }>(
			'insertLinesMutation',
			({ payload }: { payload: IDSSLinesModel.Root }) =>
				api
					.post(`Lines/czml` + get(getPlanQueryParameter), {
						json: payload
					})
					.json<T>()
		),
	useValidateAndVisualizeLinesMutation: <T>() =>
		useMutation<T, HTTPError, { input: IDSSLinesModel.Root }>(
			'validateAndVisualizeLinesMutation',
			({ input }: { input: IDSSLinesModel.Root }) =>
				api.post('Lines/temp/czml' + get(getPlanQueryParameter), { json: input }).json<T>()
		)
};

const damages = {
	useAddReportMutation: <T>() =>
		useMutation<T, HTTPError, { damageReport: T }>(
			'useAddReportMutation',
			({ damageReport }: { damageReport: T }) =>
				api.post(`Damages/czml` + get(getPlanQueryParameter), { json: damageReport }).json()
		)
};

const lineBaseData = {
	useUpdateLineBaseData: <ILineBaseData>({
		mutationOptions
	}: {
		mutationOptions?: UseMutationOptions<
			ILineBaseData,
			HTTPError,
			{ lineBaseData: ILineBaseData },
			unknown
		>;
	}) =>
		useMutation<ILineBaseData, HTTPError, { lineBaseData: ILineBaseData }>(
			'putLineBaseData',
			({ lineBaseData }: { lineBaseData: ILineBaseData }) =>
				api.put(`LineBaseData`, { json: lineBaseData }).json<ILineBaseData>(),
			mutationOptions
		),
	useCreateLineBaseDataMutation: <ILineBaseData>({
		mutationOptions
	}: {
		mutationOptions?: UseMutationOptions<
			ILineBaseData,
			HTTPError,
			{ lineBaseData: ILineBaseData },
			unknown
		>;
	}) =>
		useMutation<ILineBaseData, HTTPError, { lineBaseData: ILineBaseData }>(
			'postLineBaseData',
			({ lineBaseData }: { lineBaseData: ILineBaseData }) =>
				api.post(`LineBaseData`, { json: lineBaseData }).json<ILineBaseData>(),
			mutationOptions
		),
	useDeleteLineBaseDataMutation: <Response>({
		mutationOptions
	}: {
		mutationOptions?: UseMutationOptions<Response, HTTPError, { lineBaseDataId: number }, unknown>;
	}) =>
		useMutation<Response, HTTPError, { lineBaseDataId: number }>(
			'deleteLineBaseData',
			({ lineBaseDataId }: { lineBaseDataId: number }) =>
				api.delete(`LineBaseData/${lineBaseDataId}`).json(),
			mutationOptions
		)
};

const companiesAndUsers = {
	useUpdateCompanyMutation: <T extends ICompanyModel>() =>
		useMutation<T, HTTPError, { company: T }>('putCompany', ({ company }: { company: T }) =>
			api.put(`Companies/${company.companyId}`, { json: company }).json()
		),
	useUpdateUserPermissionsMutation: <
		T extends { userProfileId: number; permissions: string[] }
	>() =>
		useMutation<T, HTTPError, { updateUserPermissionsPayload: T }>(
			['putUserPermissions'],
			({ updateUserPermissionsPayload }: { updateUserPermissionsPayload: T }) =>
				api.put(`Account/EditUserPermissions`, { json: updateUserPermissionsPayload }).json()
		),
	useUpdateUserRoleMutation: <T extends { userProfileId: number; roleName: string }>() =>
		useMutation<T, HTTPError, { updateUserRolePayload: T }>(
			['putUserRole'],
			({ updateUserRolePayload }: { updateUserRolePayload: T }) =>
				api.put(`Account/EditUserRole`, { json: updateUserRolePayload }).json()
		),
	useToggleUserActiveStatusMutation: <T extends { userProfileId: number }>() =>
		useMutation<T, HTTPError, { user: T }>(['toggleUserActiveStatus'], ({ user }: { user: T }) =>
			api.put(`Account/ChangeActiveStatus`, { json: user }).json()
		),
	useDeleteUserMutation: <T extends { userProfileId: number }>() =>
		useMutation<T, HTTPError, { user: T }>(['deleteUser'], ({ user }: { user: T }) =>
			api.delete(`Account/DeleteUser`, { json: user }).json()
		),
	useUpdateCompanyPartnershipMutation: <T extends IUpdatePartnershipModel>() =>
		useMutation<T, HTTPError, { input: T }>('putCompanyPartnership', ({ input }: { input: T }) =>
			api.put(`Companies/${input.companyId}/companypartnership`, { json: input }).json()
		),
	useAddCompanyPartnershipMutation: <T extends IAddPartnershipModel>() =>
		useMutation<T, HTTPError, { input: T }>('postCompanyPartnership', ({ input }: { input: T }) =>
			api.post(`Companies/${input.companyId}/companypartnership`, { json: input }).json()
		),
	useUpdateCompanySettingsMutation: <
		T extends { autoCreateUser: boolean; autoActivateUser: boolean }
	>() =>
		useMutation<T, HTTPError, { companyId: number; input: T }>(
			'putCompanySettings',
			({ companyId, input }: { companyId: number; input: T }) =>
				api.put(`Companies/settings/${companyId}`, { json: input }).json()
		)
};

const aviationObstacles = {
	useAviationObstacleDataRefreshQuery: <T>() =>
		useMutation<T, HTTPError, { companyId: number; input: T }>(
			'useAviationObstacleDataRefreshQuery',
			({ companyId, input }: { companyId: number; input: T }) =>
				api.put(`DataRefresh/AviationObstacles/company/${companyId}`, { json: input }).json()
		),
	useUpdateNrlDatavaskProgress: <T>() =>
		useMutation<T, HTTPError, { nrlConfirmedDatavaskSuccess?: boolean; nrlSyncStartDate?: string }>(
			'updateNrlDatavaskProgress',
			({
				nrlConfirmedDatavaskSuccess,
				nrlSyncStartDate
			}: {
				nrlConfirmedDatavaskSuccess?: boolean;
				nrlSyncStartDate?: string;
			}) =>
				api
					.put(`AviationObstacles/updateNrlDatavaskProgress`, {
						json: { nrlConfirmedDatavaskSuccess, nrlSyncStartDate }
					})
					.json()
		),
	useAviationObstacleReportQuery: <T>() =>
		useMutation<Blob, HTTPError, { input: T }>(
			'useAviationObstacleReportQuery',
			({ input }: { input: T }) => api.post('Reports/aviationobstacles', { json: input }).blob(),
			{
				onSuccess(data) {
					const url = window.URL.createObjectURL(data);
					window.open(url);
					URL.revokeObjectURL(url);
				},
				onError(err) {
					if (err instanceof HTTPError) {
						console.error(err.response);
					} else {
						console.error(err);
					}
				}
			}
		),
	useAviationObstacleNrlSyncQuery: <Response>() =>
		useMutation<Response, HTTPError, { companyId: number }>(
			'useAviationObstacleNrlSyncQuery',
			({ companyId }: { companyId: number }) =>
				api.patch(`AviationObstacles/NrlSyncOfLiveData?companyId=${companyId}`).json()
		),
	useReadAviationObstacleDiffQuery: <T>() =>
		useMutation<T, HTTPError, { companyId: number }>(
			'useReadAviationObstacleDiffQuery',
			({ companyId }: { companyId: number }) =>
				api.get(`AviationObstacles/ReadLiveToNrlQuery?companyId=${companyId}`).json()
		),
	useAviationObstacleChangeNrlStatusMutation: <T>() =>
		useMutation<T, HTTPError, { companyId: number; input: T }>(
			'useAviationObstacleChangeNrlStatusMutation',
			({ companyId, input }: { companyId: number; input: T }) =>
				api
					.patch(`AviationObstacles/ManualDeleteNrlObject?companyId=${companyId}`, { json: input })
					.json()
		),
	useResetNrlDatavaskProgressMutation: <T>() =>
		useMutation<T, HTTPError, { companyId: number }>(
			'useResetNrlDatavaskProgress',
			({ companyId }: { companyId: number }) =>
				api.put(`AviationObstacles/resetNrlDatavaskProgress?companyId=${companyId}`).json()
		)
};

const images = {
	useAddMastPointImageFileMutation: <Response>({
		mutationOptions
	}: {
		mutationOptions?: UseMutationOptions<
			Response,
			HTTPError,
			{ mastPointId: number; formData: FormData },
			unknown
		>;
	}) =>
		useMutation<Response, HTTPError, { mastPointId: number; formData: FormData }>(
			'addMastPointFile',
			({ mastPointId, formData }: { mastPointId: number; formData: FormData }) =>
				api
					.post(`MastPointFile/${mastPointId}`, {
						body: formData
					})
					.json(),
			mutationOptions
		),
	useDeleteMastPointImageFileMutation: <Response>({
		mutationOptions
	}: {
		mutationOptions?: UseMutationOptions<
			Response,
			APIErrorModel,
			{ mastPointFileId: number; mastPointId: number },
			unknown
		>;
	}) =>
		useMutation<Response, APIErrorModel, { mastPointFileId: number; mastPointId: number }>(
			'deleteMastPointFile',
			({ mastPointFileId, mastPointId }: { mastPointFileId: number; mastPointId: number }) =>
				api.delete(`MastPointFile/${mastPointId}/${mastPointFileId}`).json(),
			mutationOptions
		),
	useAddCompanyLogoMutation: <Response>({
		mutationOptions
	}: {
		mutationOptions?: UseMutationOptions<
			Response,
			HTTPError,
			{ companyId: number; formData: FormData },
			unknown
		>;
	}) =>
		useMutation<Response, HTTPError, { companyId: number; formData: FormData }>(
			'addMastPointFile',
			({ companyId, formData }: { companyId: number; formData: FormData }) =>
				api
					.put(`Companies/${companyId}/logo`, {
						body: formData
					})
					.json(),
			mutationOptions
		),
	useFindMastCandidatesForSingleImage: <T extends ISmartData.MastCandidate[]>() =>
		useMutation<T, HTTPError, { payload: ISmartData.MastCandidatePayload }>(
			'useFindMastCandidateForSingleImage',
			({ payload }: { payload: ISmartData.MastCandidatePayload }) =>
				api
					.post('FileManager/FindCandidatesForImage', {
						json: payload
					})
					.json()
		),
	useFindMastCandidatesForMultipleImages: <T extends ISmartData.MastCandidate[]>() =>
		useMutation<T, HTTPError, { payload: ISmartData.MastCandidatePayload[] }>(
			'useFindMastCandidatesForMultipleImages',
			({ payload }: { payload: ISmartData.MastCandidatePayload[] }) =>
				api
					.post('FileManager/FindCandidatesForImages', {
						json: payload
					})
					.json()
		)
};

const files = {
	useExportPlanMutation: <T>() =>
		useMutation<Blob, HTTPError, { polygon: IGeoJsonPolygonModel['polygon'] }>(
			'useExportPlanMutation',
			(polygon) =>
				api.post('PolygonExports/File' + get(getPlanQueryParameter), { json: polygon }).blob()
		),
	/**
	 * Requests a SAS URL from the backend for direct upload to Azure Blob Storage.
	 * Returns the SAS URL that the client uses to upload the file directly.
	 */
	useGetPointCloudUploadUrl: <T>({
		mutationOptions
	}: {
		mutationOptions?: UseMutationOptions<
			T,
			HTTPError,
			{ originalFileName: string; name: string; description: string },
			unknown
		>;
	}) =>
		useMutation<T, HTTPError, { originalFileName: string; name: string; description: string }>(
			'useGetPointCloudUploadUrl',
			({ originalFileName, name, description }) =>
				api
					.post(`PointCloud/CreateUploadUrl`, {
						json: { originalFileName, name, description }
					})
					.json(),
			mutationOptions
		),
	/**
	 * Same as above, but for images
	 */
	useGetImageUploadUrl: <T>({
		mutationOptions
	}: {
		mutationOptions?: UseMutationOptions<T, HTTPError, ImageUploadDataEnrichment, unknown>;
	}) =>
		useMutation<T, HTTPError, ImageUploadDataEnrichment>(
			'useGetImageUploadUrl',
			(payload: ImageUploadDataEnrichment) =>
				api
					.post(`FileManager/CreateUploadUrl`, {
						json: payload
					})
					.json(),
			mutationOptions
		),
	useFinalizeImageUpload: <T>({
		mutationOptions
	}: {
		mutationOptions?: UseMutationOptions<
			T,
			HTTPError,
			{ imageIds: number[]; autoConnectToMastpoint: boolean },
			unknown
		>;
	}) =>
		useMutation<T, HTTPError, { imageIds: number[]; autoConnectToMastpoint: boolean }>(
			'useFinalizeImageUpload',
			({
				imageIds,
				autoConnectToMastpoint
			}: {
				imageIds: number[];
				autoConnectToMastpoint: boolean;
			}) =>
				api
					.post('FileManager/FinalizeUpload', {
						json: { imageIds, autoConnectToMastpoint }
					})
					.json(),
			mutationOptions
		),
	useEditPointCloudMutation: <T>({
		mutationOptions
	}: {
		mutationOptions?: UseMutationOptions<
			T,
			HTTPError,
			{ assetId: number; input: { name: string; description: string } },
			unknown
		>;
	}) =>
		useMutation<T, HTTPError, { assetId: number; input: { name: string; description: string } }>(
			'useEditPointCloudMutation',
			({ assetId, input }: { assetId: number; input: { name: string; description: string } }) =>
				api
					.put(`PointCloud/${assetId}/UpdateInfo`, {
						json: input
					})
					.json(),
			mutationOptions
		),
	useDeletePointCloudMutation: <T>({
		mutationOptions
	}: {
		mutationOptions?: UseMutationOptions<T, HTTPError, { assetId: number }, unknown>;
	}) =>
		useMutation<T, HTTPError, { assetId: number }>(
			'useDeletePointCloudMutation',
			({ assetId }: { assetId: number }) => api.delete(`PointCloud/${assetId}`).json(),
			mutationOptions
		),
	useFinalizePointCloudUploadMutation: <T>({
		mutationOptions
	}: {
		mutationOptions?: UseMutationOptions<T, HTTPError, { assetId: number }, unknown>;
	}) =>
		useMutation<T, HTTPError, { assetId: number }>(
			'useFinalizePointCloudUploadMutation',
			({ assetId }: { assetId: number }) => api.post(`PointCloud/${assetId}/FinalizeUpload`).json(),
			mutationOptions
		),
	useProcessPointCloudMutation: ({
		mutationOptions
	}: {
		mutationOptions?: UseMutationOptions<
			Response,
			HTTPError,
			{
				assetId: number;
				sourceCrs?: string;
				manualCrs?: string;
				manualHorizontalCrs?: string;
				manualVerticalCrs?: string;
			},
			unknown
		>;
	}) =>
		useMutation<
			Response,
			HTTPError,
			{
				assetId: number;
				sourceCrs?: string;
				manualCrs?: string;
				manualHorizontalCrs?: string;
				manualVerticalCrs?: string;
			}
		>(
			'useProcessPointCloudMutation',
			({
				assetId,
				sourceCrs,
				manualCrs,
				manualHorizontalCrs,
				manualVerticalCrs
			}: {
				assetId: number;
				sourceCrs?: string;
				manualCrs?: string;
				manualHorizontalCrs?: string;
				manualVerticalCrs?: string;
			}) =>
				api.post(`PointCloud/${assetId}/Process`, {
					json: {
						SourceCrs: sourceCrs,
						ManualCrs: manualCrs,
						ManualHorizontalCrs: manualHorizontalCrs,
						ManualVerticalCrs: manualVerticalCrs
					}
				}),
			mutationOptions
		),
	useAddPlanFileMutation: <T>({
		mutationOptions
	}: {
		mutationOptions?: UseMutationOptions<
			T,
			HTTPError,
			{ planId: number; input: FormData },
			unknown
		>;
	}) =>
		useMutation<T, HTTPError, { planId: number; input: FormData }>(
			'useAddPlanFileMutation',
			({ input, planId }: { input: FormData; planId: number }) =>
				api
					.post(`Plans/attachments/${planId}`, {
						body: input
					})
					.json(),
			mutationOptions
		),
	useDeletePlanFileMutation: <T>({
		mutationOptions
	}: {
		mutationOptions?: UseMutationOptions<
			T,
			HTTPError,
			{ planId: number; attachmentId: number },
			unknown
		>;
	}) =>
		useMutation<T, HTTPError, { planId: number; attachmentId: number }>(
			'useDeletePlanFileMutation',
			({ planId, attachmentId }: { planId: number; attachmentId: number }) =>
				api.delete(`Plans/attachments/${planId}/${attachmentId}`).json(),
			mutationOptions
		),
	useUpdateImageMutation: <T>({
		mutationOptions
	}: {
		mutationOptions?: UseMutationOptions<
			T,
			HTTPError,
			{ imageId: number; payload: ISmartData.UpdateImageData },
			unknown
		>;
	}) =>
		useMutation<T, HTTPError, { imageId: number; payload: ISmartData.UpdateImageData }>(
			'useUpdateImageMutation',
			({ imageId, payload }: { imageId: number; payload: ISmartData.UpdateImageData }) =>
				api.put(`FileManager/${imageId}`, { json: payload }).json(),
			mutationOptions
		),
	useDeleteImageMutation: <T>({
		mutationOptions
	}: {
		mutationOptions?: UseMutationOptions<T, HTTPError, { imageId: number }, unknown>;
	}) =>
		useMutation<T, HTTPError, { imageId: number }>(
			'useDeleteImageMutation',
			({ imageId }: { imageId: number }) => api.delete(`FileManager/${imageId}`).json(),
			mutationOptions
		),
	/**
	 * Renames a folder. Both paths are full paths — only the last segment differs,
	 * since renaming must not move the folder within the hierarchy. The response
	 * is a bare status code, so nothing is parsed.
	 */
	useRenameFolderMutation: <T>({
		mutationOptions
	}: {
		mutationOptions?: UseMutationOptions<T, HTTPError, { path: string; newPath: string }, unknown>;
	}) =>
		useMutation<T, HTTPError, { path: string; newPath: string }>(
			'useRenameFolderMutation',
			// Status-code-only response — the body is never parsed (it may be empty).
			({ path, newPath }: { path: string; newPath: string }) =>
				api.put('FileManager/folders/rename', {
					json: { path, newPath }
				}) as unknown as Promise<T>,
			mutationOptions
		)
};

const utilityLinks = {
	useNewUtilityLinkMutation: <T>() =>
		useMutation<T, HTTPError, { template: T }>(
			'useNewUtilityLinkMutation',
			({ template }: { template: T }) =>
				api.post(`UtilityLink/construction`, { json: template }).json()
		)
};

const crossbeams = {
	useUpdateCrossbeamMutation: <T>({
		mutationOptions
	}: {
		mutationOptions?: UseMutationOptions<
			T,
			HTTPError,
			{ mastId: number; payload: IMastPointPutModel.CrossbeamPutData },
			unknown
		>;
	}) =>
		useMutation<T, HTTPError, { mastId: number; payload: IMastPointPutModel.CrossbeamPutData }>(
			'useUpdateCrossbeamMutation',
			({ mastId, payload }: { mastId: number; payload: IMastPointPutModel.CrossbeamPutData }) =>
				api
					.put(`MastPoint/${mastId}/czml` + get(getPlanQueryParameter), {
						json: {
							crossBeam: payload
						}
					})
					.json(),
			mutationOptions
		)
};
const importData = {
	useStartNisImportMutation: <T>() =>
		useMutation<T, HTTPError, { planId: string; label: string }>(
			'useStartNisImportMutation',
			({ planId, label }: { planId: string; label: string }) =>
				api
					.post(`Plans/getPlandataFromNis`, {
						json: { planId, label }
					})
					.json<T>()
		),
	useWritePlandataToNisMutation: <T>() =>
		useMutation<T, HTTPError, { cId: number; planId: number }>(
			'useWritePlandataToNisMutation',
			({ cId, planId }: { cId: number; planId: number }) =>
				api.post(`Plans/writePlandataToNis?planId=${planId}`).json<T>()
		)
};

export interface IPlanClimateLoadsPayload {
	windLoad: number;
	iceLoad: number;
	returnTimeWind: number;
	returnTimeIce: number;
	combinedReturnTime: number;
	isIceLoadByDistrictClass: boolean;
}

const section = {
	usePostPolygonForSectionInfo: <T>({
		mutationOptions
	}: {
		mutationOptions?: UseMutationOptions<
			T,
			HTTPError,
			{ polygon: IGeoJsonPolygonModel['polygon']; distanceControlLineTerrain: boolean },
			unknown
		>;
	}) =>
		useMutation<
			T,
			HTTPError,
			{ polygon: IGeoJsonPolygonModel['polygon']; distanceControlLineTerrain: boolean }
		>(
			'postPolygonForSectionInfo',
			({
				polygon,
				distanceControlLineTerrain
			}: {
				polygon: IGeoJsonPolygonModel['polygon'];
				distanceControlLineTerrain: boolean;
			}) =>
				api
					.post(`Section/PolygonSection/info` + get(getPlanQueryParameter), {
						json: { polygon, distanceControlLineTerrain }
					})
					.json(),
			mutationOptions
		),
	usePostPolygonForCalculation: <T>({
		mutationOptions
	}: {
		mutationOptions?: UseMutationOptions<T, HTTPError, ISectionCalculationPayload, unknown>;
	}) =>
		useMutation<T, HTTPError, ISectionCalculationPayload>(
			'postPolygonForCalculation',
			({
				includeCalculationFile,
				polygon,
				distanceControlLineTerrain,
				sectionCircuits,
				loadCases,
				planId
			}: ISectionCalculationPayload) =>
				api
					.post(
						`Section/PolygonSection/calculate` +
							(planId ? `?planId=${planId}` : get(getPlanQueryParameter)),
						{
							json: {
								includeCalculationFile,
								polygon,
								distanceControlLineTerrain,
								sectionCircuits,
								loadCases
							}
						}
					)
					.json(),
			mutationOptions
		),
	/**
	 * Actually belongs to projects (plan) where it is used as a mutation, but is utilized in Section.
	 * Todo: Move to projects object.
	 */
	usePutPlanClimateLoadsMutation: <T>({
		mutationOptions
	}: {
		mutationOptions?: UseMutationOptions<
			T,
			HTTPError,
			{ planId: number; payload: IPlanClimateLoadsPayload },
			unknown
		>;
	}) =>
		useMutation<T, HTTPError, { planId: number; payload: IPlanClimateLoadsPayload }>(
			'putPlanClimateLoadsMutation',
			({ planId, payload }: { planId: number; payload: IPlanClimateLoadsPayload }) =>
				api.put(`Plans/${planId}/climateloads`, { json: payload }).json(),
			mutationOptions
		),
	usePostMontageStretchMutation: <T>({
		mutationOptions
	}: {
		mutationOptions?: UseMutationOptions<
			T,
			HTTPError,
			{ sectionCircuits: ISectionCalculationCircuit[] },
			unknown
		>;
	} = {}) =>
		useMutation<T, HTTPError, { sectionCircuits: ISectionCalculationCircuit[] }>(
			'postMontageStretchMutation',
			({ sectionCircuits }: { sectionCircuits: ISectionCalculationCircuit[] }) =>
				api
					.post(`Section/PolygonSection/lines/montagestretch` + get(getPlanQueryParameter), {
						json: { sectionCircuits }
					})
					.json(),
			mutationOptions
		)
};

const mutations = {
	section,
	conditions,
	projects,
	damages,
	mastPoints,
	poles,
	lines,
	lineBaseData,
	companiesAndUsers,
	aviationObstacles,
	images,
	files,
	utilityLinks,
	crossbeams,
	supports,
	importData
};

export { mutations };
